import React, { useState } from 'react';
import DesktopNotificationControl from './DesktopNotificationControl';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  ListGroup,
  Row,
  Spinner
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Notification from './Notification';
import c from 'util/const';
import config from 'util/config';
import { inboxNotificationTarget } from 'util/notificationRouting';
import { notificationMessage } from 'util/billingNotificationTime';
import {
  useGetNotificationInboxQuery,
  useMarkInboxReadMutation,
  useMarkAllInboxReadMutation
} from 'rtk/notificationInbox.slice';
import Pagination from 'components/common/Pagination';
import Background from 'components/common/Background';
import Flex from 'components/common/Flex';
import SoftBadge from 'components/common/SoftBadge';
import corner6 from 'assets/img/illustrations/corner-6.png';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faBellSlash,
  faCheck,
  faCheckCircle,
  faCheckDouble,
  faExclamationTriangle,
  faRedo,
  faSync
} from '@fortawesome/free-solid-svg-icons';

export default function NotificationInbox() {
  const userGuid = useSelector(state => state.app.userMaster?.GUID);
  const isAdmin = useSelector(state =>
    state.authentication?.userRoles?.includes(c.roles.TENANT_ADMIN)
  );
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [readError, setReadError] = useState('');

  const query = useGetNotificationInboxQuery(
    {
      userGuid,
      indexStart: pageIndex * pageSize,
      maxResults: pageSize,
      unreadOnly
    },
    {
      skip: !userGuid,
      pollingInterval: config.signalRUrl ? 300000 : 30000,
      skipPollingIfUnfocused: true,
      refetchOnMountOrArgChange: true
    }
  );
  const [markRead, readState] = useMarkInboxReadMutation();
  const [markAllRead, allState] = useMarkAllInboxReadMutation();
  const page = query.currentData?.Data ?? query.currentData ?? {};
  const items = page.Objects || [];

  const saveRead = async guid => {
    try {
      setReadError('');
      await (guid ? markRead(guid) : markAllRead()).unwrap();
    } catch {
      setReadError('Unable to mark notifications as read. Please try again.');
    }
  };

  return (
    <Card>
      <Card.Header className="position-relative px-card py-3 bg-light border-bottom">
        <Background
          image={corner6}
          className="bg-card rounded-top"
          style={{ pointerEvents: 'none' }}
        />
        <Row className="align-items-center justify-content-between g-2 position-relative">
          <Col xs="auto" className="d-flex align-items-center">
            <div className="avatar avatar-m me-2 d-none d-sm-block">
              <div className="avatar-name rounded-circle bg-primary-subtle text-primary">
                <FontAwesomeIcon icon={faBell} className="fs-1 text-primary" />
              </div>
            </div>
            <div>
              <h4 className="mb-0 text-900 d-flex align-items-center gap-2">
                Notifications
                {page.UnreadCount > 0 && (
                  <SoftBadge bg="primary" pill className="fs--2">
                    {page.UnreadCount} new
                  </SoftBadge>
                )}
              </h4>
              <p className="text-600 fs--1 mb-0">
                Manage your alerts and activity updates
              </p>
            </div>
          </Col>
          <Col xs="auto">
            <Flex alignItems="center" className="gap-2">
              <Button
                variant="falcon-default"
                size="sm"
                onClick={() => query.refetch()}
                disabled={query.isFetching}
                title="Refresh notifications"
              >
                <FontAwesomeIcon
                  icon={faSync}
                  spin={query.isFetching}
                  className="me-1"
                />
                Refresh
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={() => saveRead(null)}
                disabled={allState.isLoading || !page.UnreadCount}
              >
                <FontAwesomeIcon icon={faCheckDouble} className="me-1" />
                Mark all as read
              </Button>
            </Flex>
          </Col>
        </Row>
      </Card.Header>

      <Card.Body className="bg-light border-bottom py-2 px-card">
        <Flex
          justifyContent="between"
          alignItems="center"
          className="flex-wrap gap-2"
        >
          <Flex alignItems="center" className="gap-2">
            <Form.Check
              type="switch"
              id="notification-unread-only"
              label="Unread only"
              className="mb-0 align-items-center fs--1 text-700 fw-semi-bold"
              checked={unreadOnly}
              onChange={event => {
                setUnreadOnly(event.target.checked);
                setPageIndex(0);
              }}
            />
            {page.UnreadCount > 0 && (
              <SoftBadge bg="warning" pill className="fs--2">
                {page.UnreadCount} unread
              </SoftBadge>
            )}
          </Flex>

          <Flex alignItems="center" className="gap-2 text-muted fs--1">
            <DesktopNotificationControl />
          </Flex>
        </Flex>
      </Card.Body>

      {readError && (
        <Alert
          variant="danger"
          className="m-card mb-0 d-flex align-items-center justify-content-between"
          dismissible
          onClose={() => setReadError('')}
        >
          <span>{readError}</span>
        </Alert>
      )}

      {query.isLoading && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" role="status">
            <span className="visually-hidden">Loading notifications</span>
          </Spinner>
          <p className="text-600 mt-2 fs--1 mb-0">Loading notifications...</p>
        </div>
      )}

      {query.error && (
        <Alert
          variant="danger"
          className="m-card d-flex align-items-center justify-content-between"
        >
          <div className="d-flex align-items-center">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="me-2 text-danger"
            />
            <span>Unable to load notifications.</span>
          </div>
          <Button size="sm" variant="outline-danger" onClick={query.refetch}>
            <FontAwesomeIcon icon={faRedo} className="me-1" />
            Retry
          </Button>
        </Alert>
      )}

      {!query.isLoading && !query.error && items.length === 0 && (
        <div className="text-center py-5">
          <div className="avatar avatar-3xl mb-3 mx-auto">
            <div className="avatar-name rounded-circle bg-200 text-600">
              <FontAwesomeIcon
                icon={unreadOnly ? faCheckCircle : faBellSlash}
                className="fs-2 text-400"
              />
            </div>
          </div>
          <h5 className="text-800 mb-1">
            {unreadOnly ? 'No unread notifications' : 'No notifications'}
          </h5>
          <p
            className="text-500 fs--1 mb-3"
            style={{ maxWidth: 360, margin: '0 auto' }}
          >
            {unreadOnly
              ? "You're all caught up! You don't have any unread notifications."
              : 'There are no notifications in your inbox at this time.'}
          </p>
          {unreadOnly && (
            <Button
              variant="falcon-default"
              size="sm"
              onClick={() => {
                setUnreadOnly(false);
                setPageIndex(0);
              }}
            >
              View all notifications
            </Button>
          )}
        </div>
      )}

      {!query.isLoading && items.length > 0 && (
        <ListGroup variant="flush" className="fs--1">
          {items.map(item => (
            <ListGroup.Item
              key={item.GUID}
              className={classNames(
                'px-card py-3 d-flex align-items-center justify-content-between gap-3 transition-base',
                {
                  'bg-light': item.Unread,
                  'bg-white': !item.Unread
                }
              )}
              style={{
                borderLeft: item.Unread
                  ? '4px solid var(--falcon-primary, #2c7be5)'
                  : '4px solid transparent'
              }}
            >
              <div
                className="flex-grow-1"
                style={{ minWidth: 0, cursor: 'pointer' }}
                onClick={() => item.Unread && saveRead(item.GUID)}
              >
                <Notification
                  time={item.CreatedUtc}
                  to={inboxNotificationTarget(item, isAdmin, userGuid)}
                  unread={item.Unread}
                  flush
                  plainText
                  avatar={{
                    name: item.Title || 'Jurat',
                    size: 'xl',
                    rounded: 'circle'
                  }}
                >
                  {notificationMessage(item)}
                </Notification>
              </div>
              {item.Unread && (
                <Button
                  size="sm"
                  variant="falcon-default"
                  className="text-primary text-nowrap"
                  disabled={readState.isLoading}
                  onClick={() => saveRead(item.GUID)}
                >
                  <FontAwesomeIcon icon={faCheck} className="me-1" />
                  Mark as read
                </Button>
              )}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}

      {!query.isLoading && (page.TotalRecords > 0 || items.length > 0) && (
        <Card.Footer className="bg-light py-2">
          <Pagination
            className="py-0"
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalRecords={page.TotalRecords || 0}
            currentCount={items.length}
            onPageChange={setPageIndex}
            onPageSizeChange={size => {
              setPageSize(size);
              setPageIndex(0);
            }}
            endOfResults={page.EndOfResults !== false}
            disabled={query.isLoading || query.isFetching}
            pageSizeOptions={[10, 20, 50]}
            onRefresh={() => query.refetch()}
            loading={query.isFetching}
          />
        </Card.Footer>
      )}
    </Card>
  );
}
